import {
  Box,
  IconButton,
  Image,
  Switch,
  Text,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { RiDeleteBinLine, RiFileEditLine } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import CTable from "../../../components/Table/table";
import {
  activateAnnouncementRequest,
  deleteAnnouncementRequest,
} from "../../../modules/admin-modules/announcements/Actions";
import { openCreateAnnouncement } from "../../../modules/admin-modules/models/Actions";
import { API } from "../../../constants";
import { brandPalette } from "../../../constants/brandPalette";

const C = {
  primary: brandPalette.primary,
  secondary: brandPalette.accentDark,
  border: brandPalette.stone,
};

const iconBtnProps = {
  variant: "ghost",
  border: "0",
  size: "sm",
  color: C.primary,
  _hover: { bg: "rgba(91, 13, 22, 0.08)", color: C.secondary },
};

const AnnouncementsTable = ({ setAdd, setSelectedData, searchTerm = "" }) => {
  const announcements = useSelector((state) => state.announcements.announcements);
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.announcements.isLoading);
  const [, setPage] = useState(0);
  const [size, setPerPage] = useState(10);
  const [pageNumber, setPageNumber] = useState(0);
  const baseUrl = API || "https://api.edutopiaonline.com/";

  const list = useMemo(() => {
    const raw = Array.isArray(announcements) ? announcements : [];
    const q = (searchTerm || "").trim().toLowerCase();
    if (!q) return raw;
    return raw.filter((row) => {
      const title = String(row?.title ?? "").toLowerCase();
      const content = String(row?.content ?? "").toLowerCase();
      return title.includes(q) || content.includes(q);
    });
  }, [announcements, searchTerm]);

  useEffect(() => {
    setPageNumber(0);
  }, [searchTerm]);

  const viewData = (row) => {
    const plain = list.find((r) => r.id === row?.id) ?? row;
    setSelectedData(plain);
    setAdd(false);
    dispatch(openCreateAnnouncement());
  };

  const Data = list.map((data) => ({
    ...data,
    id: data?.id,
    title: data?.title,
    content: (
      <Text maxW="280px" noOfLines={2} color="var(--text-primary)" fontSize="sm">
        {data?.content || "—"}
      </Text>
    ),
    image: data?.image ? (
      <Image
        src={`${baseUrl}${data.image}`}
        alt={data.title}
        maxW="80px"
        maxH="80px"
        objectFit="cover"
        borderRadius="md"
        border="1px solid"
        borderColor="var(--border-subtle)"
      />
    ) : (
      <Text color="var(--text-muted)" fontSize="sm">
        No image
      </Text>
    ),
    active: (
      <Box onClick={(e) => e.stopPropagation()}>
        <Switch
          isChecked={data?.active === 1}
          colorScheme="green"
          onChange={() => {
            dispatch(activateAnnouncementRequest(data?.id));
          }}
        />
      </Box>
    ),
    edit: (
      <IconButton
        {...iconBtnProps}
        aria-label="Edit announcement"
        fontSize="20px"
        icon={<RiFileEditLine />}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedData(data);
          setAdd(false);
          dispatch(openCreateAnnouncement());
        }}
      />
    ),
    delete: (
      <IconButton
        {...iconBtnProps}
        aria-label="Delete announcement"
        fontSize="20px"
        icon={<RiDeleteBinLine color="#9b2c2c" />}
        onClick={(e) => {
          e.stopPropagation();
          dispatch(deleteAnnouncementRequest(data?.id));
        }}
      />
    ),
  }));

  const columns = [
    { Header: "Title", accessor: "title" },
    { Header: "Content", accessor: "content" },
    { Header: "Image", accessor: "image" },
    { Header: "Active", accessor: "active" },
    { Header: "Edit", accessor: "edit" },
    { Header: "Delete", accessor: "delete" },
  ];

  const totalPage = Math.max(1, Math.ceil(list.length / size));

  return (
    <Box px={{ base: 4, md: 8 }} pb={10} w="100%">
      <Box
        bg="var(--surface-raised)"
        border="1px solid"
        borderColor={C.border}
        borderRadius="16px"
        overflow="hidden"
        boxShadow="sm"
      >
        <CTable
          selectedData={viewData}
          Columns={columns}
          Data={Data}
          path="openModel"
          Title=""
          subTitle=""
          btnTitle=""
          placeHolder=""
          noSearchBar
          noFilter
          footerBtnTitle
          filterList
          addButton
          addButtonTitle=""
          setPage={setPage}
          setPerPage={setPerPage}
          currentpage={pageNumber}
          setPageNumber={setPageNumber}
          noSecondSearchBar
          isLoading={isLoading}
          perPage={size}
          totalPage={totalPage}
          wrapperProps={{ maxH: "unset", overflow: "auto" }}
          tableProps={{ bg: "transparent", borderRadius: "0", mb: 0 }}
          theadProps={{ bg: "var(--surface-muted)", color: "var(--text-primary)" }}
        />
      </Box>
    </Box>
  );
};

export default AnnouncementsTable;
